<script lang="ts" setup>
import { useResponsive } from "@composables"
import { TRACKING_EVENTS } from "@constant"
import { ClimType, PacType, SystemChauffage } from "@machine/form.machine"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useFormStore } from "@stores/form.store"
import { toTypedSchema } from "@vee-validate/yup"
import { useField, useForm } from "vee-validate"
import { computed, inject, onActivated, onDeactivated, ref, watch } from "vue"
import { array, object, string, date } from "yup"

const { snapshot, send } = useFormStore()

const { isMobile } = useResponsive()

const { $track } = inject<TrackingPlugin>("tracking")!

const optionsSystemeChauffage = [
    {
        label: "Sèche serviette électrique",
        value: "seche_serviette_elec",
    },
    {
        label: "Plancher chauffant électrique",
        value: "plancher_chauffant_elec",
    },
    {
        label: "Pompe à chaleur (PAC)",
        value: "pac",
    },
    {
        label: "Radiateurs électriques fixes",
        value: "radiateur_elec_fixe",
    },
    {
        label: "Radiateurs d'appoints",
        value: "radiateur_elec_mobile",
    },
    {
        label: "Autre/Je ne sais pas",
        value: "autre",
    },
] as {
    label: string
    value: SystemChauffage
    children: { label: string; value: string }[]
}[]

const optionsClimatisation = [
    {
        label: "Climatiseur mobile",
        value: "clim_mobile",
    },
    {
        label: "Climatiseur fixe ou PAC air/air réversible",
        value: "clim_fix",
    },
    {
        label: "Autre/Je ne sais pas",
        value: "autre",
    },
    {
        label: "Pas de climatisation",
        value: "no_clim",
    },
] as {
    label: string
    value: ClimType
}[]

const optionsPac = [
    {
        label: "PAC air/air",
        value: "pac_air_air",
    },
    {
        label: "PAC air/eau",
        value: "pac_air_eau",
    },
    {
        label: "PAC eau/eau",
        value: "pac_eau_eau",
    },
    {
        label: "Autre/Je ne sais pas",
        value: "autre",
    },
] as { label: string; value: PacType }[]

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            chauffage_principale_avant_travaux: string().default(
                snapshot.value.context.chauffage_principale_avant_travaux
            ),
            chauffage_principale_apres_travaux: string().default(
                snapshot.value.context.chauffage_principale_apres_travaux
            ),
            type_systeme_chauffage_elec_avant_travaux: array()
                .of(string())
                .default(
                    snapshot.value.context
                        .type_systeme_chauffage_elec_avant_travaux
                ),
            type_systeme_chauffage_elec_apres_travaux: array()
                .of(string())
                .default(
                    snapshot.value.context
                        .type_systeme_chauffage_elec_apres_travaux
                ),
            type_climatisation_avant_travaux: string().default(
                snapshot.value.context.type_climatisation_avant_travaux
            ),
            type_climatisation_apres_travaux: string().default(
                snapshot.value.context.type_climatisation_apres_travaux
            ),
            type_pac_avant_travaux: array()
                .of(string())
                .default(snapshot.value.context.type_pac_avant_travaux),
            type_pac_apres_travaux: array()
                .of(string())
                .default(snapshot.value.context.type_pac_apres_travaux),
            periode_abscence: array()
                .of(
                    object({
                        work: string(),
                        period: array().of(date()).min(2),
                    })
                )
                .default(snapshot.value.context.periode_abscence),
        })
    ),
})

function onError() {
    // Envoie un événement de suivi
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: snapshot.value.context.formId,
        step: snapshot.value.context.step + 1,
    })
}

function onSuccess(values: any) {
    // mise à jour du context
    ;[
        "type_systeme_chauffage_elec_avant_travaux",
        "type_systeme_chauffage_elec_apres_travaux",
        "type_climatisation_avant_travaux",
        "type_climatisation_apres_travaux",
        "chauffage_principale_avant_travaux",
        "chauffage_principale_apres_travaux",
        "type_pac_avant_travaux",
        "type_pac_apres_travaux",
    ].forEach((key) => {
        send({
            type: "UPDATE_FIELD",
            field: key,
            value: values[key],
        })
    })

    // Pour les péridoes, on ne les met pas à jour si elles sont vides
    const periodeAbscenceComplet = values.periode_abscence.filter(
        (periode: { travaux: string; period: [Date, Date] }) =>
            periode.travaux !== "" &&
            periode.period[0] !== null &&
            periode.period[1] !== null
    )

    send({
        type: "UPDATE_FIELD",
        field: "periode_abscence",
        value: periodeAbscenceComplet,
    })

    send({
        type: "SUBMIT",
    })
}

const onSubmit = handleSubmit(onSuccess, onError)

const { value: type_systeme_chauffage_elec_avant_travaux } = useField<string[]>(
    "type_systeme_chauffage_elec_avant_travaux"
)
const { value: type_systeme_chauffage_elec_apres_travaux } = useField<string[]>(
    "type_systeme_chauffage_elec_apres_travaux"
)

const { value: type_climatisation_apres_travaux } = useField(
    "type_climatisation_apres_travaux"
)
const { value: type_climatisation_avant_travaux } = useField(
    "type_climatisation_avant_travaux"
)

const { value: chauffage_principale_apres_travaux } = useField(
    "chauffage_principale_apres_travaux"
)
const { value: chauffage_principale_avant_travaux } = useField(
    "chauffage_principale_avant_travaux"
)

const { value: type_pac_avant_travaux } = useField<string[]>(
    "type_pac_avant_travaux"
)
const { value: type_pac_apres_travaux } = useField<string[]>(
    "type_pac_apres_travaux"
)
const { value: periode_abscence } = useField("periode_abscence")

function getOptionsSystemeChauffage(
    type: boolean = false
): { label: string; value: SystemChauffage }[] {
    // récupère seulement les options de chauffage sélectionnées
    const selectedOptions =
        (!type
            ? type_systeme_chauffage_elec_avant_travaux.value
            : type_systeme_chauffage_elec_apres_travaux.value) ?? []

    return optionsSystemeChauffage.filter((option) =>
        selectedOptions.includes(option.value)
    )
}

watch(
    [
        type_systeme_chauffage_elec_avant_travaux,
        type_systeme_chauffage_elec_apres_travaux,
    ],
    () => {
        // Reset les champs de chauffage principale si le système de chauffage change
        if (type_systeme_chauffage_elec_avant_travaux.value.length <= 1) {
            chauffage_principale_avant_travaux.value = ""
        } else {
            // Met le champ de chauffage principale à la première option
            chauffage_principale_avant_travaux.value =
                type_systeme_chauffage_elec_avant_travaux.value[0]
        }
        if (type_systeme_chauffage_elec_apres_travaux.value.length <= 1) {
            chauffage_principale_apres_travaux.value = ""
        } else {
            // Met le champ de chauffage principale à la première option
            chauffage_principale_apres_travaux.value =
                type_systeme_chauffage_elec_apres_travaux.value[0]
        }
    }
)

const timeCompletion = ref(new Date().getTime())

function isDisplaySystemeChauffage(before: boolean = false): boolean {
    // Affiche le système de chauffage si l'utilisateur a sélectionné au moins un système de chauffage
    return before
        ? snapshot.value.context.usage_energie_elec_avant_travaux.find(
              (usage: string) => usage === "chauffage"
          )
        : snapshot.value.context.usage_energie_elec_apres_travaux.find(
              (usage: string) => usage === "chauffage"
          )
}

const isDisplaySystemeChauffageSection = computed(() => {
    return isDisplaySystemeChauffage(true) || isDisplaySystemeChauffage()
})

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})
</script>

<template>
    <FormCard
        class="form-optional"
        title="Facultatif, mais utile"
        confirm-label="Valider et terminer"
        header-btn
        :horizontal-btn="isMobile"
        @submit="
            () => {
                onSubmit()
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-optional',
                    buttonName: 'Valider et terminer',
                })
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-optional',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="snapshot.matches({ Step3: 'updateField' })"
    >
        <!-- Système de chauffage -->
        <fieldset
            class="flex flex-col md:flex-row"
            v-if="isDisplaySystemeChauffageSection"
        >
            <legend class="text-primary-800 font-bold mb-4">
                Quel(s) système(s) de chauffage électrique utilisez-vous ?
            </legend>
            <!-- Avant travaux-->
            <div
                class="flex flex-col gap-4 w-full"
                v-if="isDisplaySystemeChauffage(true)"
            >
                <!-- Liste des systèmes de chauffage -->
                <BaseCheckboxList
                    label="Avant travaux"
                    :options="optionsSystemeChauffage"
                    name="systeme_chauffage_avant_travaux"
                    input-id="systeme_chauffage_avant_travaux"
                    option-label="label"
                    option-value="value"
                    v-model="type_systeme_chauffage_elec_avant_travaux"
                >
                    <template
                        #option-pac
                        v-if="
                            type_systeme_chauffage_elec_avant_travaux.includes(
                                'pac'
                            )
                        "
                    >
                        <BaseCheckboxList
                            :options="optionsPac"
                            name="type_pac_avant_travaux"
                            input-id="type_pac_avant_travaux"
                            option-label="label"
                            option-value="value"
                            v-model="type_pac_avant_travaux"
                            class="pl-8 pt-2"
                        >
                        </BaseCheckboxList>
                    </template>
                </BaseCheckboxList>
                <!-- Système principal -->
                <BaseInputWrapper
                    v-if="type_systeme_chauffage_elec_avant_travaux.length > 1"
                    label="Indiquez le système de chauffage principal avant travaux"
                    class="w-full"
                    input-id="chauffage_principale_avant_travaux"
                    :error="errors.chauffage_principale_avant_travaux"
                >
                    <BaseSelect
                        placeholder="Saisissez"
                        input-id="chauffage_principale_avant_travaux"
                        option-label="label"
                        option-value="value"
                        :options="getOptionsSystemeChauffage()"
                        v-model="chauffage_principale_avant_travaux"
                    />
                </BaseInputWrapper>
            </div>
            <!-- Après travaux-->
            <div
                class="flex flex-col gap-4 w-full"
                v-if="isDisplaySystemeChauffage(false)"
            >
                <!-- Liste des systèmes de chauffage -->
                <BaseCheckboxList
                    label="Après travaux"
                    :options="optionsSystemeChauffage"
                    name="systeme_chauffage_apres_travaux"
                    input-id="systeme_chauffage_apres_travaux"
                    option-label="label"
                    option-value="value"
                    v-model="type_systeme_chauffage_elec_apres_travaux"
                >
                    <template
                        #option-pac
                        v-if="
                            type_systeme_chauffage_elec_apres_travaux.includes(
                                'pac'
                            )
                        "
                    >
                        <BaseCheckboxList
                            :options="optionsPac"
                            name="type_pac_apres_travaux"
                            input-id="type_pac_apres_travaux"
                            option-label="label"
                            option-value="value"
                            v-model="type_pac_apres_travaux"
                            class="pl-8 pt-2"
                        >
                        </BaseCheckboxList>
                    </template>
                </BaseCheckboxList>
                <!-- Système principal -->
                <BaseInputWrapper
                    v-if="type_systeme_chauffage_elec_apres_travaux.length > 1"
                    label="Indiquez le système de chauffage principal après travaux"
                    class="w-full"
                    input-id="chauffage_apres_travaux"
                    :error="errors.chauffage_principale_apres_travaux"
                >
                    <BaseSelect
                        placeholder="Saisissez"
                        input-id="chauffage_apres_travaux"
                        option-label="label"
                        option-value="value"
                        :invalid="!!errors.chauffage_principale_apres_travaux"
                        :options="getOptionsSystemeChauffage(true)"
                        v-model="chauffage_principale_apres_travaux"
                    />
                </BaseInputWrapper>
            </div>
        </fieldset>
        <!-- Mode de climatisation -->
        <fieldset class="flex-col md:flex-row">
            <legend class="text-primary-800 font-bold mb-4">
                Utilisez-vous la climatisation ? Si oui, précisez le type
                d'appareil
            </legend>
            <!-- Avant travaux -->
            <BaseInputWrapper
                input-id="climatisation_avant_travaux"
                label="Avant travaux"
                class="w-full"
                :error="errors.type_climatisation_avant_travaux"
            >
                <BaseSelect
                    placeholder="Saisissez"
                    :options="optionsClimatisation"
                    option-label="label"
                    option-value="value"
                    v-model="type_climatisation_avant_travaux"
                    :invalid="!!errors.type_climatisation_avant_travaux"
                    input-id="climatisation_avant_travaux"
                />
            </BaseInputWrapper>
            <!-- Après travaux -->
            <BaseInputWrapper
                input-id="climatisation_apres_travaux"
                label="Après travaux"
                class="w-full"
                :error="errors.type_climatisation_apres_travaux"
            >
                <BaseSelect
                    placeholder="Saisissez"
                    :options="optionsClimatisation"
                    option-label="label"
                    option-value="value"
                    v-model="type_climatisation_apres_travaux"
                    :invalid="!!errors.type_climatisation_apres_travaux"
                    input-id="climatisation_apres_travaux"
                />
            </BaseInputWrapper>
        </fieldset>
        <!-- Abscence -->
        <fieldset>
            <legend class="text-primary-800 font-bold mb-4">
                Si le logement est resté inoccupé plus de 2 semaines avant et/ou
                après les travaux, indiquez les dates
            </legend>
            <InputAbscence v-model="periode_abscence" class="w-full" />
        </fieldset>
    </FormCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";
.form-optional {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);
    }

    @media (--small-viewport) {
        --gap-form: 2.5rem;
        --padding-form: 1rem;
    }

    fieldset {
        display: flex;
        gap: 2rem;
        width: 100%;
    }
}
</style>
